/*
 * Shared argument and permission checks for the agent tools. Every check
 * returns an error string (shown to the model as an error tool_result) or
 * null, so executors never throw on bad input.
 */

/**
 * Resolve the 1-based group number from a tool input.
 * @returns {{ index: number, group: object } | { error: string }}
 */
export function resolveGroup(ctx, number) {
    const groups = ctx.getGroups();
    const index = number - 1;
    if (!Number.isInteger(number) || !groups[index]) {
        return { error: `Group ${number} does not exist. The project has ${groups.length} group(s), numbered from 1.` };
    }
    return { index, group: groups[index] };
}

/** Writes are refused on groups locked by the user, the parent window or the role. */
export function checkWritable(ctx, index) {
    const group = ctx.getGroups()[index];
    if (ctx.isLocked(index)) {
        return `Group ${index + 1} "${group?.name ?? ""}" is locked, so it cannot be changed. Ask the user to unlock it first.`;
    }
    return null;
}

/**
 * Resolve 1-based event numbers to 0-based indexes.
 * @returns {{ indexes: number[] } | { error: string }}
 */
export function resolveEvents(group, numbers) {
    const count = group.events?.length ?? 0;
    const bad = numbers.filter((n) => !Number.isInteger(n) || n < 1 || n > count);
    if (bad.length) {
        return { error: `Event number(s) ${bad.slice(0, 10).join(", ")} do not exist. The group has ${count} event(s), numbered from 1.` };
    }
    return { indexes: [...new Set(numbers.map((n) => n - 1))].sort((a, b) => a - b) };
}

const METADATA_FIELDS = { notes: "notes", approved: "approvals", forced: "forced", tags: "tags" };

/** Metadata edits follow the team/iframe role permissions. */
export function checkMetadataFields(ctx, fields) {
    for (const field of fields) {
        const kind = METADATA_FIELDS[field];
        if (kind && !ctx.canEditMetadata(kind)) {
            return `Editing ${field} is not allowed for this user's role.`;
        }
    }
    return null;
}

export function isTime(value) {
    return typeof value === "number" && Number.isFinite(value) && value >= 0;
}
